"use client";

import { Button, TextInput } from "@carbon/react";
import {
  Add,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderAdd,
  OverflowMenuVertical,
  TrashCan,
} from "@carbon/react/icons";
import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  useActionState,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  createChatFolderAction,
  deleteChatFolderAction,
  deleteConversationAction,
  moveConversationFolderAction,
  renameChatFolderAction,
  type ChatFolderFormState,
} from "@/app/(app)/shell-actions";
import { ConfirmDialog, CreateDialog } from "@/components/CreateDialog";
import { FormShell } from "@/components/FormShell";
import { chatFolderCopy, groupChatNav } from "@/lib/ui/chat-folder";
import {
  chatDeleteMessage,
  chatDeletePopoverStyle,
  chatHistoryCopy,
  conversationHref,
  isConversationActive,
  isNewChatActive,
  newChatHref,
  type ChatFolderHistoryItem,
  type ChatHistoryItem,
} from "@/lib/ui/chat-history";
import { orgCopy } from "@/lib/ui/org";

const folderFormInitial: ChatFolderFormState = { error: null, saved: false };
const CHAT_FOLDER_OPEN_KEY = "pinwiki.chat-folder-open";

function readStoredFolderOpen(): Record<string, boolean> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(CHAT_FOLDER_OPEN_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    const out: Record<string, boolean> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") {
        out[id] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeStoredFolderOpen(state: Record<string, boolean>) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(CHAT_FOLDER_OPEN_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

function useChatNavPopover(params: {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  busy?: boolean;
  onClose: () => void;
}) {
  const { open, triggerRef, panelRef, busy = false, onClose } = params;
  const [themeClass, setThemeClass] = useState("cds--white");
  const [panelStyle, setPanelStyle] = useState<ReturnType<
    typeof chatDeletePopoverStyle
  > | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      return;
    }
    const update = () => {
      if (!triggerRef.current) {
        return;
      }
      const themeRoot = triggerRef.current.closest(
        ".cds--white, .cds--g10, .cds--g90, .cds--g100",
      );
      if (themeRoot) {
        const name = ["cds--g100", "cds--g90", "cds--g10", "cds--white"].find(
          (item) => themeRoot.classList.contains(item),
        );
        if (name) {
          setThemeClass(name);
        }
      }
      setPanelStyle(
        chatDeletePopoverStyle({
          trigger: triggerRef.current.getBoundingClientRect(),
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        }),
      );
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [busy, onClose, open, panelRef, triggerRef]);

  return { themeClass, panelStyle };
}

function ChatNavPopover({
  open,
  titleId,
  themeClass,
  panelStyle,
  panelRef,
  children,
}: {
  open: boolean;
  titleId: string;
  themeClass: string;
  panelStyle: ReturnType<typeof chatDeletePopoverStyle> | null;
  panelRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  if (!open || !panelStyle) {
    return null;
  }
  return createPortal(
    <div
      ref={panelRef}
      className={`chat-nav-delete-pop chat-nav-delete-pop--caret-${panelStyle.caretSide} ${themeClass}`}
      role="dialog"
      aria-labelledby={titleId}
      style={
        {
          top: panelStyle.top,
          left: panelStyle.left,
          "--chat-delete-caret-top": `${panelStyle.caretTop}px`,
        } as CSSProperties
      }
    >
      <span className="chat-nav-delete-pop-caret" aria-hidden />
      {children}
    </div>,
    document.body,
  );
}

export function ChatNavList({
  items,
  folders,
}: {
  items: ChatHistoryItem[];
  folders: ChatFolderHistoryItem[];
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [pending, setPending] = useState<ChatHistoryItem | null>(null);
  const [moveChat, setMoveChat] = useState<ChatHistoryItem | null>(null);
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(folders.map((folder) => [folder.id, true])),
  );
  const [renameFolder, setRenameFolder] = useState<ChatFolderHistoryItem | null>(
    null,
  );
  const [deleteFolder, setDeleteFolder] = useState<ChatFolderHistoryItem | null>(
    null,
  );
  const [deletingFolder, startDeleteFolder] = useTransition();
  const grouped = groupChatNav({ folders, chats: items });

  useEffect(() => {
    const stored = readStoredFolderOpen();
    setOpenFolders((prev) => {
      const next = { ...prev };
      for (const folder of folders) {
        if (stored[folder.id] !== undefined) {
          next[folder.id] = stored[folder.id]!;
        } else if (next[folder.id] === undefined) {
          next[folder.id] = true;
        }
      }
      const active = items.find((chat) =>
        isConversationActive(pathname, chat.id),
      );
      if (active?.folderId) {
        next[active.folderId] = true;
      }
      writeStoredFolderOpen(next);
      return next;
    });
  }, [folders, items, pathname]);

  const setFolderOpen = (folderId: string, open: boolean) => {
    setOpenFolders((prev) => {
      const next = { ...prev, [folderId]: open };
      writeStoredFolderOpen(next);
      return next;
    });
  };

  return (
    <>
      <li className="chat-nav-folder-add-host">
        <ChatNewIcon />
        <ChatFolderCreateIcon />
      </li>
      {grouped.folders.map(({ folder, chats }) => {
        const expanded = openFolders[folder.id] !== false;
        return (
          <li key={folder.id} className="cds--side-nav__menu-item chat-nav-folder">
            <div className="chat-nav-folder-row">
              {/* Expand/collapse folder — not a page action. */}
              <button
                type="button"
                className="chat-nav-folder-toggle"
                aria-expanded={expanded}
                aria-label={
                  expanded ? chatFolderCopy.collapse : chatFolderCopy.expand
                }
                onClick={() => setFolderOpen(folder.id, !expanded)}
              >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="chat-nav-folder-name">{folder.name}</span>
                <span className="chat-nav-folder-count">{chats.length}</span>
              </button>
              <ChatFolderActions
                onRename={() => setRenameFolder(folder)}
                onDelete={() => setDeleteFolder(folder)}
              />
            </div>
            {expanded ? (
              <ul className="chat-nav-folder-list">
                {chats.length === 0 ? (
                  <li className="chat-nav-folder-empty">{chatFolderCopy.empty}</li>
                ) : (
                  chats.map((chat) => (
                    <ChatNavRow
                      key={chat.id}
                      chat={chat}
                      folders={folders}
                      pathname={pathname}
                      pending={pending}
                      moveOpen={moveChat?.id === chat.id}
                      onPending={setPending}
                      onMoveOpen={() => {
                        setPending(null);
                        setMoveChat(chat);
                      }}
                      onMoveClose={() => setMoveChat(null)}
                    />
                  ))
                )}
              </ul>
            ) : null}
          </li>
        );
      })}
      {grouped.unfiled.map((chat) => (
        <ChatNavRow
          key={chat.id}
          chat={chat}
          folders={folders}
          pathname={pathname}
          pending={pending}
          moveOpen={moveChat?.id === chat.id}
          onPending={setPending}
          onMoveOpen={() => {
            setPending(null);
            setMoveChat(chat);
          }}
          onMoveClose={() => setMoveChat(null)}
        />
      ))}
      {renameFolder ? (
        <ChatFolderRename
          folder={renameFolder}
          onClose={() => setRenameFolder(null)}
        />
      ) : null}
      <ConfirmDialog
        open={deleteFolder !== null}
        title={chatFolderCopy.deleteTitle}
        danger
        primaryLabel={chatFolderCopy.deleteConfirm}
        secondaryLabel={chatFolderCopy.deleteCancel}
        busy={deletingFolder}
        onClose={() => {
          if (!deletingFolder) {
            setDeleteFolder(null);
          }
        }}
        onSubmit={() => {
          if (!deleteFolder) {
            return;
          }
          const id = deleteFolder.id;
          startDeleteFolder(async () => {
            await deleteChatFolderAction(id);
            setDeleteFolder(null);
            router.refresh();
          });
        }}
      >
        <p>{chatFolderCopy.deleteHelp}</p>
      </ConfirmDialog>
    </>
  );
}

function ChatNavRow({
  chat,
  folders,
  pathname,
  pending,
  moveOpen,
  onPending,
  onMoveOpen,
  onMoveClose,
}: {
  chat: ChatHistoryItem;
  folders: ChatFolderHistoryItem[];
  pathname: string;
  pending: ChatHistoryItem | null;
  moveOpen: boolean;
  onPending: (chat: ChatHistoryItem | null) => void;
  onMoveOpen: () => void;
  onMoveClose: () => void;
}) {
  const current = isConversationActive(pathname, chat.id);

  return (
    <li
      className={[
        "cds--side-nav__menu-item chat-nav-item",
        current ? "chat-nav-item--current" : "",
        pending?.id === chat.id || moveOpen ? "is-confirm" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <NextLink
        href={conversationHref(chat.id)}
        className={`cds--side-nav__link${current ? " cds--side-nav__link--current" : ""}`}
        title={chat.title}
      >
        <span className="chat-nav-dot" aria-hidden />
        <span className="cds--side-nav__link-text">{chat.title}</span>
        {chat.when ? <span className="chat-nav-when">{chat.when}</span> : null}
      </NextLink>
      <ChatNavMove
        chat={chat}
        folders={folders}
        open={moveOpen}
        onOpen={onMoveOpen}
        onClose={onMoveClose}
      />
      <ChatNavDelete
        chat={chat}
        open={pending?.id === chat.id}
        onOpen={() => {
          onMoveClose();
          onPending(chat);
        }}
        onClose={() => onPending(null)}
      />
    </li>
  );
}

function ChatNewIcon() {
  const pathname = usePathname();
  const router = useRouter();
  const active = isNewChatActive(pathname);

  return (
    /* Icon on «Чаты» title — not a page action. */
    <button
      type="button"
      className={`chat-nav-title-action chat-nav-new${active ? " is-on" : ""}`}
      aria-label={chatHistoryCopy.newChat}
      aria-current={active ? "page" : undefined}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        router.push(newChatHref());
      }}
    >
      <Add size={16} />
    </button>
  );
}

function ChatFolderCreateIcon() {
  const router = useRouter();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, startSave] = useTransition();
  const { themeClass, panelStyle } = useChatNavPopover({
    open,
    triggerRef,
    panelRef,
    busy: pending,
    onClose: () => {
      if (!pending) {
        setOpen(false);
        setError(null);
      }
    },
  });

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  return (
    <>
      {/* Icon on «Чаты» title — not a page action. */}
      <button
        ref={triggerRef}
        type="button"
        className="chat-nav-title-action chat-nav-folder-add"
        aria-label={chatFolderCopy.create}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setError(null);
          setName("");
          setOpen((value) => !value);
        }}
      >
        <FolderAdd size={16} />
      </button>
      <ChatNavPopover
        open={open}
        titleId={titleId}
        themeClass={themeClass}
        panelStyle={panelStyle}
        panelRef={panelRef}
      >
        <p className="chat-nav-delete-pop-title" id={titleId}>
          {chatFolderCopy.create}
        </p>
        <label className="chat-nav-folder-pop-label" htmlFor="chat-folder-name">
          {chatFolderCopy.nameLabel}
        </label>
        <input
          ref={inputRef}
          id="chat-folder-name"
          className="chat-nav-folder-pop-input"
          name="name"
          value={name}
          disabled={pending}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
        />
        {error ? <p className="chat-nav-folder-pop-error">{error}</p> : null}
        <div className="chat-nav-delete-pop-actions">
          <Button
            type="button"
            kind="ghost"
            size="sm"
            disabled={pending}
            onClick={() => {
              setOpen(false);
              setError(null);
            }}
          >
            {chatFolderCopy.deleteCancel}
          </Button>
          <Button
            type="button"
            kind="primary"
            size="sm"
            disabled={pending || !name.trim()}
            onClick={() => {
              startSave(async () => {
                const formData = new FormData();
                formData.set("name", name);
                const next = await createChatFolderAction(
                  folderFormInitial,
                  formData,
                );
                if (next.error) {
                  setError(next.error);
                  return;
                }
                setOpen(false);
                setName("");
                setError(null);
                router.refresh();
              });
            }}
          >
            {pending ? chatFolderCopy.pending : chatFolderCopy.create}
          </Button>
        </div>
      </ChatNavPopover>
    </>
  );
}

function ChatNavMove({
  chat,
  folders,
  open,
  onOpen,
  onClose,
}: {
  chat: ChatHistoryItem;
  folders: ChatFolderHistoryItem[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const router = useRouter();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const inputId = useId();
  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, startBusy] = useTransition();
  const { themeClass, panelStyle } = useChatNavPopover({
    open,
    triggerRef,
    panelRef,
    busy,
    onClose: () => {
      if (!busy) {
        setMode("pick");
        setName("");
        setError(null);
        onClose();
      }
    },
  });

  useEffect(() => {
    if (!open) {
      setMode("pick");
      setName("");
      setError(null);
      return;
    }
    if (mode === "create") {
      inputRef.current?.focus();
    }
  }, [open, mode]);

  const pick = (folderId: string | null) => {
    startBusy(async () => {
      await moveConversationFolderAction(chat.id, folderId);
      onClose();
      router.refresh();
    });
  };

  const createAndMove = () => {
    startBusy(async () => {
      const formData = new FormData();
      formData.set("name", name);
      const next = await createChatFolderAction(folderFormInitial, formData);
      if (next.error || !next.folderId) {
        setError(next.error ?? chatFolderCopy.unknown);
        return;
      }
      await moveConversationFolderAction(chat.id, next.folderId);
      setMode("pick");
      setName("");
      setError(null);
      onClose();
      router.refresh();
    });
  };

  return (
    <>
      {/* Icon in the side nav row, not a page action. */}
      <button
        ref={triggerRef}
        type="button"
        className="chat-nav-move"
        aria-label={chatFolderCopy.move}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (open) {
            onClose();
            return;
          }
          onOpen();
        }}
      >
        <Folder size={16} />
      </button>
      <ChatNavPopover
        open={open}
        titleId={titleId}
        themeClass={themeClass}
        panelStyle={panelStyle}
        panelRef={panelRef}
      >
        {mode === "pick" ? (
          <>
            <p className="chat-nav-delete-pop-title" id={titleId}>
              {chatFolderCopy.move}
            </p>
            <ul className="chat-nav-folder-pick">
              <li>
                <button
                  type="button"
                  className="chat-nav-folder-pick-item"
                  disabled={busy || chat.folderId === null}
                  onClick={() => pick(null)}
                >
                  {chatFolderCopy.none}
                </button>
              </li>
              {folders.map((folder) => (
                <li key={folder.id}>
                  <button
                    type="button"
                    className="chat-nav-folder-pick-item"
                    disabled={busy || chat.folderId === folder.id}
                    onClick={() => pick(folder.id)}
                  >
                    {folder.name}
                  </button>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  className="chat-nav-folder-pick-item chat-nav-folder-pick-item--create"
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    setName("");
                    setMode("create");
                  }}
                >
                  {chatFolderCopy.moveCreate}
                </button>
              </li>
            </ul>
            <div className="chat-nav-delete-pop-actions">
              <Button
                type="button"
                kind="ghost"
                size="sm"
                disabled={busy}
                onClick={onClose}
              >
                {chatFolderCopy.deleteCancel}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="chat-nav-delete-pop-title" id={titleId}>
              {chatFolderCopy.create}
            </p>
            <label className="chat-nav-folder-pop-label" htmlFor={inputId}>
              {chatFolderCopy.nameLabel}
            </label>
            <input
              ref={inputRef}
              id={inputId}
              className="chat-nav-folder-pop-input"
              name="name"
              value={name}
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (name.trim()) {
                    createAndMove();
                  }
                }
              }}
            />
            {error ? <p className="chat-nav-folder-pop-error">{error}</p> : null}
            <div className="chat-nav-delete-pop-actions">
              <Button
                type="button"
                kind="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setMode("pick");
                  setName("");
                  setError(null);
                }}
              >
                {chatFolderCopy.deleteCancel}
              </Button>
              <Button
                type="button"
                kind="primary"
                size="sm"
                disabled={busy || !name.trim()}
                onClick={createAndMove}
              >
                {busy ? chatFolderCopy.pending : chatFolderCopy.createAndMove}
              </Button>
            </div>
          </>
        )}
      </ChatNavPopover>
    </>
  );
}

function ChatFolderActions({
  onRename,
  onDelete,
}: {
  onRename: () => void;
  onDelete: () => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const { themeClass, panelStyle } = useChatNavPopover({
    open,
    triggerRef,
    panelRef,
    busy: false,
    onClose: () => setOpen(false),
  });

  return (
    <>
      {/* Kebab in folder row — Carbon OverflowMenu fails under SideNav capture. */}
      <button
        ref={triggerRef}
        type="button"
        className="chat-nav-folder-menu"
        aria-label={chatFolderCopy.actions}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <OverflowMenuVertical size={16} />
      </button>
      <ChatNavPopover
        open={open}
        titleId={titleId}
        themeClass={themeClass}
        panelStyle={panelStyle}
        panelRef={panelRef}
      >
        <p className="chat-nav-delete-pop-title" id={titleId}>
          {chatFolderCopy.actions}
        </p>
        <ul className="chat-nav-folder-pick">
          <li>
            <button
              type="button"
              className="chat-nav-folder-pick-item"
              onClick={() => {
                setOpen(false);
                onRename();
              }}
            >
              {chatFolderCopy.rename}
            </button>
          </li>
          <li>
            <button
              type="button"
              className="chat-nav-folder-pick-item chat-nav-folder-pick-item--danger"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              {chatFolderCopy.remove}
            </button>
          </li>
        </ul>
        <div className="chat-nav-delete-pop-actions">
          <Button
            type="button"
            kind="ghost"
            size="sm"
            onClick={() => setOpen(false)}
          >
            {chatFolderCopy.deleteCancel}
          </Button>
        </div>
      </ChatNavPopover>
    </>
  );
}

function ChatFolderRename({
  folder,
  onClose,
}: {
  folder: ChatFolderHistoryItem;
  onClose: () => void;
}) {
  const router = useRouter();
  const [formKey, setFormKey] = useState(0);
  const [staleError, setStaleError] = useState(true);
  const [state, formAction, pending] = useActionState(
    async (prev: ChatFolderFormState, formData: FormData) => {
      const next = await renameChatFolderAction(folder.id, prev, formData);
      setStaleError(false);
      if (next.saved && !next.error) {
        onClose();
        router.refresh();
      }
      return next;
    },
    folderFormInitial,
  );

  useEffect(() => {
    setFormKey((key) => key + 1);
    setStaleError(true);
  }, [folder.id]);

  return (
    <CreateDialog
      openLabel={chatFolderCopy.rename}
      title={chatFolderCopy.renameTitle}
      pending={pending}
      open
      hideTrigger
      focusSelector="#chat-folder-rename"
      onOpen={() => undefined}
      onClose={onClose}
    >
      <FormShell
        key={formKey}
        layout="dialog"
        action={formAction}
        error={state.error && !staleError ? state.error : null}
        errorTitle={chatFolderCopy.errorTitle}
        submitLabel={chatFolderCopy.rename}
        pendingLabel={chatFolderCopy.pending}
        pending={pending}
        cancelLabel={orgCopy.cancel}
        onCancel={onClose}
      >
        <TextInput
          id="chat-folder-rename"
          name="name"
          labelText={chatFolderCopy.nameLabel}
          defaultValue={folder.name}
          required
        />
      </FormShell>
    </CreateDialog>
  );
}

function ChatNavDelete({
  chat,
  open,
  onOpen,
  onClose,
}: {
  chat: ChatHistoryItem;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [deleting, startDelete] = useTransition();
  const { themeClass, panelStyle } = useChatNavPopover({
    open,
    triggerRef,
    panelRef,
    busy: deleting,
    onClose,
  });

  return (
    <>
      {/* Icon in the side nav row, not a page action. */}
      <button
        ref={triggerRef}
        type="button"
        className="chat-nav-delete"
        aria-label={chatHistoryCopy.delete}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (open) {
            onClose();
            return;
          }
          onOpen();
        }}
      >
        <TrashCan size={16} />
      </button>
      <ChatNavPopover
        open={open}
        titleId={titleId}
        themeClass={themeClass}
        panelStyle={panelStyle}
        panelRef={panelRef}
      >
        <p className="chat-nav-delete-pop-title" id={titleId}>
          {chatHistoryCopy.deleteTitle}
        </p>
        <p className="chat-nav-delete-pop-text">
          {chatDeleteMessage(chat.title)}
        </p>
        <div className="chat-nav-delete-pop-actions">
          <Button
            type="button"
            kind="ghost"
            size="sm"
            disabled={deleting}
            onClick={onClose}
          >
            {chatHistoryCopy.deleteCancel}
          </Button>
          <Button
            type="button"
            kind="danger"
            size="sm"
            disabled={deleting}
            onClick={() => {
              startDelete(async () => {
                const ok = await deleteConversationAction(chat.id);
                onClose();
                if (ok && isConversationActive(pathname, chat.id)) {
                  router.push("/chat");
                }
                router.refresh();
              });
            }}
          >
            {chatHistoryCopy.deleteConfirm}
          </Button>
        </div>
      </ChatNavPopover>
    </>
  );
}
